import { useEffect, useRef, useState } from "react";
import { IconButton, Slider, Tooltip, Typography } from '@material-ui/core';
import { formatPercentage } from '@roc/ui/utils';
import { withStyles } from '@material-ui/core/styles';
import { Box } from '@material-ui/core';
import { observer } from 'mobx-react';
import { makeStyles, Theme } from '@material-ui/core/styles';
import { AddCircleOutline, InfoOutlined, RemoveCircleOutline } from "@material-ui/icons";
import { PercentageField } from "@roc/ui";

export type RateSliderProps = {
  rate: number;
  minRate: number;
  maxRate: number;
  yieldSpread: number;
  onChange: (value: number) => void;
};

const sliderStyles: any = (theme: Theme) => ({
  root: ({ min, max, rate, ysp }) => {
    const trackWidth = ((rate - min) / (max - min)) * 100;
    const yspLeft = trackWidth > 40 ? (trackWidth / 2) - 20 : 0;
    return {
      paddingBottom: '30px',
      position: 'relative',
      '& .MuiSlider-root': {
        padding: '20px 0  !important',
      },
      '& .MuiSlider-rail': {
        height: '4px',
        borderRadius: '2px',
      },
      '& .MuiSlider-track': {
        height: '4px',
        width: `${trackWidth}%  !important`,
      },
      '& .MuiSlider-mark': {
        display: 'none',
      },
      '& .yieldSpread': {
        position: 'absolute',
        left: `${yspLeft}%`,
        top: '55px',
        fontSize: '12px',
        textAlign: 'center',
        textWrap: 'nowrap',
        width: '0px',
        textIndent: '10px',
        zIndex: 1,
      },
    };
  },
  thumb: {
    position: 'absolute',
    width: 0,
    height: 0,
    color: theme,
    outline: 'none',
  },
  parRateThumb: {
    color: 'rgba(0,0,0,0.8)',
    top: '51px',
    pointerEvents: 'none',
    marginTop: '4px',
    '& $thumbIcons': {
      transform: 'rotate(180deg)',
    },
  },
  borrowerRateThumb: {
    color: theme.palette.primary.main,
    marginTop: '-47px',
  },
  thumbContent: {
    width: '200px',
    height: '50px',
    marginLeft: '-100px',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  thumbBubble: {
    padding: '0 12px',
    backgroundColor: 'currentColor',
    color: 'inherit',
    textAlign: 'center',
    borderRadius: '4px',
  },
  thumbText: {
    color: 'white',
    fontSize: '10px',
    marginTop: '-4px',
  },
  thumbValue: {
    color: 'white',
    fontSize: '14px',
    fontWeight: 'bold',
    paddingBottom: '2px',
    marginTop: '-5px',
  },
  thumbArrow: {
    width: 12,
    height: 6,
    display: 'block',
  },
  thumbPin: {
    width: 20,
    height: 17,
    display: 'block',
  },
  thumbIcons: {
    height: 25,
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
});

const useStyles = makeStyles(sliderStyles);

const ThumbArrow = ({ className }) => (
  <svg viewBox="0 0 4 2" className={className}>
    <path d="M0,0 L2,2 L4,0 Z" fill="currentColor" />
  </svg>
);

const ThumbIcon = ({ className }) => (
  <svg viewBox="0 0 9 12" className={className}>
    <path
      d="M 4.5,12
      L 1.5,4.4
      C 0.7,2.2 2.3,0.0 4.5,0.0
      C 6.8,0.0 8.3,2.7 7.6,4.3
      L 4.5,12 Z"
      fill="currentColor"
    />
  </svg>
);

const ThumbComponent = ({ muiProps, isParRate, title, interestRate, parRate }) => {
  const classes = useStyles({});
  const className = isParRate
    ? classes.parRateThumb
    : classes.borrowerRateThumb;

  const icons = (
    <div className={classes.thumbIcons}>
      <ThumbArrow className={classes.thumbArrow} />
      <ThumbIcon className={classes.thumbPin} />
    </div>
  );

  const props = isParRate ? { style: muiProps.style } : muiProps;

  return (
    <div {...props} className={`${classes.thumb} ${className}`}>
      <div className={classes.thumbContent}>
        {isParRate && icons}
        <div className={classes.thumbBubble}>
          <span className={classes.thumbText} style={{ fontSize: '12px' }}>
            {`${title}: ${isParRate
              ? `${formatPercentage(parRate)}`
              : `${formatPercentage(interestRate)}`}`}
          </span>
        </div>
        {!isParRate && icons}
      </div>
    </div>
  );
};

export const PricerRateSlider = (props: RateSliderProps) => {
  const { rate, minRate, maxRate, yieldSpread, onChange } = props;

  const [sliderValue, setSliderValue] = useState(rate);

  useEffect(() => {
    setSliderValue(rate);
  }, [rate])

  const min = minRate;
  const max = maxRate;
  const ysp = yieldSpread;
  const classes = useStyles({
    min,
    max,
    rate,
    ysp,
  });

  return (
    <Box pt={1} px={4}>
      <Box className={classes.root}>
        {rate && (
          <>
            <div className="yieldSpread">
              Yield Spread Premium:
              <Tooltip title="The Yield Spread Premium is calculated by subtracting the borrower's interest rate from the PAR rate, then multiplying the result by the total loan amount to determine the premium earned." placement='top'>
                <InfoOutlined fontSize='small' style={{
                  color: '#555',
                  marginRight: 4,
                  marginTop: 4,
                  fontSize: '12px',
                }} />
              </Tooltip>
              <b>{formatPercentage(ysp)}</b>
            </div>
            <ThumbComponent
              muiProps={props}
              interestRate={sliderValue}
              title={'PAR Rate'}
              isParRate={true}
              parRate={min}
            />
          </>
        )}
        <Slider
          value={sliderValue}
          onChange={(_, value) => { setSliderValue(value as number) }}
          onChangeCommitted={() => onChange(sliderValue)}
          step={0.01}
          min={min}
          max={max}
          style={{ minWidth: '400px', marginTop: '2rem' }}
          marks={[
            {
              value: max,
              label: max + '%',
            },
          ]}
          valueLabelDisplay="on"
          ThumbComponent={props => (
            <ThumbComponent
              muiProps={props}
              interestRate={sliderValue}
              title={'Borrower Rate'}
              isParRate={false}
              parRate={min}
            />
          )}
        />
      </Box>
    </Box>
  );
}

const useAnimatedTypographyStyles = makeStyles(theme => ({
  secondaryHeading: {
      fontSize: theme.typography.pxToRem(15),
      color: theme.palette.text.secondary,
    },
  animate: {
    color: "#5D2C6F",
    transform: "scale(1.2)",
  },
  amount: {
    color: 'black',
    fontSize: '24px',
    fontWeight: 'bold',
    margin: theme.spacing(0),
    transition: "color 0.4s ease, transform 0.4s ease",
  },
}));

export const AnimatedTypography = ({ value }) => {
  const classes = useAnimatedTypographyStyles();
  const typographyRef = useRef(null);
  const [isFirstRender, setIsFirstRender] = useState(true);

  const handleAnimation = () => {
    const element = typographyRef.current;
    if (element) {
      element.classList.add(classes.animate);
      setTimeout(() => {
        element.classList.remove(classes.animate);
      }, 400);
    }
  };

  useEffect(() => {
    if (!isFirstRender && value) {
      handleAnimation();
    } else {
      setIsFirstRender(false);
    }
  }, [value]);

  return (
    <Typography
      ref={typographyRef}
      className={classes.amount}
    >
      {value}
    </Typography>
  );
};

const usePercentagePickerStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cardLabel: {
    fontSize: '16px',
    color: theme.palette.text.secondary,
    fontWeight: 'bold',
  },
  iconButton: {
    color: theme.palette.grey[500],
  },
  percentageField: {
    '& .MuiInputBase-input': {
      textAlign: 'right',
      fontSize: '20px!important',
      fontWeight: 'bold !important',
    },
    '& .MuiTypography-colorTextSecondary': {
      fontWeight: 'bold',
    },
  },
}));

export const PercentagePicker = observer(
  ({
    fieldName,
    label,
    value,
    onChange,
    onBlur,
    min = 0,
    max = 100,
    allowIncrease = true,
    disabled = false,
  }) => {
    const classes = usePercentagePickerStyles();

    function handleMinusClick() {
      const newValue = Math.max(min, Math.min(max, value - 0.1));
      onChange(newValue);
      onBlur();
    }

    function handlePlusClick() {
      const newValue = Math.max(min, Math.min(max, value + 0.1));
      onChange(newValue);
      onBlur();
    }

    function handleBlur() {
      const newValue = Math.max(min, Math.min(max, value));
      onChange(newValue);
      onBlur();
    }

    return (
      <Box display="flex" flexDirection="column" alignItems="center">
        <Typography variant="body2" className={classes.cardLabel}>
          {label}
        </Typography>
        <Box display="flex" alignItems="center">
          <Box width={24} pr={1}>
            {value > min && (
              <IconButton
                className={classes.iconButton}
                onClick={handleMinusClick}
                disabled={disabled}
                style={{ padding: 0 }}
              >
                <RemoveCircleOutline />
              </IconButton>
            )}
          </Box>
          <Box width={80} textAlign={'right'}>
            <PercentageField
              testId={fieldName}
              value={value || 0}
              variant={'standard'}
              fullWidth={true}
              onChange={(name, value) => onChange(value)}
              disabled={disabled}
              inputClassname={classes.percentageField}
              allowNegative={false}
              decimalScale={2}
              onBlur={handleBlur}
            />
          </Box>
          <Box width={24} pl={1}>
            {value < max && allowIncrease && (
              <IconButton
                className={classes.iconButton}
                onClick={handlePlusClick}
                disabled={disabled}
                style={{ padding: 0 }}
              >
                <AddCircleOutline />
              </IconButton>
            )}
          </Box>
        </Box>
      </Box>
    );
  }
);